import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Scale, Check, X, SplitSquareHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminOnly } from "@/components/admin/AdminOnly";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/disputes")({
  head: () => ({
    meta: [
      { title: "Payment Protection disputes — Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AdminOnly>
      <AdminNav />
      <DisputesPage />
    </AdminOnly>
  ),
});

type Row = {
  id: string;
  project_id: string;
  freelancer_id: string;
  amount: number;
  net_amount: number;
  status: string;
  dispute_status: string | null;
  dispute_reason: string | null;
  disputed_at: string | null;
  disputed_by: string | null;
  partial_released_amount: number | null;
  created_at: string;
};

function DisputesPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"open" | "resolved">("open");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-disputes", filter],
    queryFn: async () => {
      let q = supabase
        .from("invoices")
        .select(
          "id, project_id, freelancer_id, amount, net_amount, status, dispute_status, dispute_reason, disputed_at, disputed_by, partial_released_amount, created_at",
        )
        .order("disputed_at", { ascending: false })
        .limit(200);
      if (filter === "open") {
        q = q.eq("dispute_status", "open");
      } else {
        q = q.in("dispute_status", [
          "resolved_released",
          "resolved_refunded",
          "resolved_split",
        ]);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const resolve = useMutation({
    mutationFn: async (args: {
      id: string;
      decision: "release" | "refund" | "split";
      amount?: number;
      note?: string;
    }) => {
      const { error } = await supabase.rpc("resolve_dispute", {
        _invoice_id: args.id,
        _decision: args.decision,
        _amount_to_freelancer: args.amount ?? undefined,
        _note: args.note ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Dispute resolved");
      qc.invalidateQueries({ queryKey: ["admin-disputes"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to resolve"),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-6">
      <AdminPageHeader
        icon={Scale}
        title="Payment Protection disputes"
        subtitle="Review open disputes and split funds between freelancer and client."
      />

      <div className="flex gap-2">
        {(["open", "resolved"] as const).map((s) => (
          <Button
            key={s}
            size="sm"
            variant={filter === s ? "default" : "outline"}
            onClick={() => setFilter(s)}
          >
            {s === "open" ? "Open" : "Resolved"}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !data?.length ? (
        <div className="rounded-2xl border bg-card/60 p-12 text-center text-sm text-muted-foreground">
          No {filter} disputes.
        </div>
      ) : (
        <div className="space-y-3">
          {data.map((row) => (
            <DisputeCard key={row.id} row={row} onResolve={resolve.mutate} pending={resolve.isPending} />
          ))}
        </div>
      )}
    </div>
  );
}

function DisputeCard({
  row,
  onResolve,
  pending,
}: {
  row: Row;
  onResolve: (args: { id: string; decision: "release" | "refund" | "split"; amount?: number; note?: string }) => void;
  pending: boolean;
}) {
  const [note, setNote] = useState("");
  const [splitAmount, setSplitAmount] = useState(
    ((row.net_amount || 0) / 2).toFixed(2),
  );
  const isOpen = row.dispute_status === "open";
  const resolvedLabel =
    row.dispute_status === "resolved_released"
      ? "Released to freelancer"
      : row.dispute_status === "resolved_refunded"
        ? "Refunded to client"
        : row.dispute_status === "resolved_split"
          ? "Split"
          : row.dispute_status || "-";

  return (
    <div className="rounded-2xl border bg-card/70 p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">
            ${Number(row.amount).toFixed(2)} • net ${Number(row.net_amount).toFixed(2)}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Invoice {row.id.slice(0, 8)} • Project {row.project_id.slice(0, 8)}
          </div>
          {row.disputed_at && (
            <div className="text-[11px] text-muted-foreground">
              Opened {new Date(row.disputed_at).toLocaleString()}
            </div>
          )}
        </div>
        <div className="rounded-full border px-2.5 py-1 text-[11px]">
          {isOpen ? "Open" : resolvedLabel}
        </div>
      </div>

      {row.dispute_reason && (
        <div className="mt-3 rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground whitespace-pre-wrap">
          {row.dispute_reason}
        </div>
      )}

      {isOpen && (
        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto]">
          <Textarea
            placeholder="Resolution note (internal)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="min-h-[70px] text-xs"
          />
          <div className="flex flex-col gap-2">
            <Button
              size="sm"
              disabled={pending}
              onClick={() => onResolve({ id: row.id, decision: "release", note })}
              className="gap-1"
            >
              <Check className="h-3.5 w-3.5" /> Release full
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={pending}
              onClick={() => onResolve({ id: row.id, decision: "refund", note })}
              className="gap-1"
            >
              <X className="h-3.5 w-3.5" /> Refund client
            </Button>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.01"
                min="0"
                max={row.net_amount}
                value={splitAmount}
                onChange={(e) => setSplitAmount(e.target.value)}
                className="h-8 w-24 text-xs"
              />
              <Button
                size="sm"
                variant="outline"
                disabled={pending}
                onClick={() =>
                  onResolve({
                    id: row.id,
                    decision: "split",
                    amount: Number(splitAmount),
                    note,
                  })
                }
                className="gap-1"
              >
                <SplitSquareHorizontal className="h-3.5 w-3.5" /> Split
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
